/**
 * Utility to print the current conversation
 * Clones the rendered chat DOM and opens it in a new window for printing
 */

interface PrintOptions {
  conversationTitle?: string;
  agentName?: string;
}

/**
 * Generates a printable HTML document from the chat messages
 * and opens the browser print dialog
 */
export function printConversation(options: PrintOptions = {}): void {
  const { conversationTitle = 'Conversation', agentName = 'AI Assistant' } = options;

  // Find the messages container in the DOM
  const messagesContainer = document.querySelector('[data-print-messages]');
  
  if (!messagesContainer) {
    console.error('Could not find messages container for printing');
    alert('Unable to print: No conversation found.');
    return;
  }

  // Clone the messages container
  const clonedContent = messagesContainer.cloneNode(true) as HTMLElement;

  // Process the cloned content for printing
  processForPrint(clonedContent);

  // Get current date for the header
  const printDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Build the print HTML document
  const printHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${conversationTitle} - Print</title>
  <style>
    /* Reset and base styles */
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      font-size: 12pt;
      line-height: 1.5;
      color: #1a1a1a;
      background: white;
      padding: 20px;
      max-width: 800px;
      margin: 0 auto;
    }
    
    /* Print header */
    .print-header {
      border-bottom: 2px solid #1976d2;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    
    .print-header h1 {
      font-size: 18pt;
      color: #1976d2;
      margin-bottom: 4px;
    }
    
    .print-header .meta {
      font-size: 10pt;
      color: #666;
    }
    
    /* Messages container - remove any inherited styles */
    .messages-container {
      display: block !important;
    }
    
    .messages-container > * {
      display: block !important;
      float: none !important;
      position: static !important;
      transform: none !important;
      flex: none !important;
      max-height: none !important;
      overflow: visible !important;
      height: auto !important;
      min-height: 0 !important;
    }
    
    /* Message styles */
    .message {
      margin-bottom: 16px;
      display: block !important;
    }
    
    .message-sender {
      font-weight: 600;
      font-size: 10pt;
      color: #1976d2;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .message-sender.user {
      color: #2e7d32;
    }
    
    .message-content {
      padding-left: 0;
      display: block !important;
    }
    
    /* Typography within messages */
    .message-content p {
      margin: 8px 0;
    }
    
    .message-content h1,
    .message-content h2,
    .message-content h3,
    .message-content h4 {
      margin: 16px 0 8px 0;
      color: #1a1a1a;
    }
    
    .message-content ul,
    .message-content ol {
      margin: 8px 0;
      padding-left: 24px;
    }
    
    .message-content li {
      margin: 4px 0;
    }
    
    /* Code blocks */
    .message-content pre {
      background: #f5f5f5;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 12px;
      overflow-x: auto;
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 10pt;
      margin: 12px 0;
    }
    
    .message-content code {
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      font-size: 10pt;
      background: #f5f5f5;
      padding: 2px 4px;
      border-radius: 3px;
    }
    
    .message-content pre code {
      background: none;
      padding: 0;
    }
    
    /* Tables */
    .message-content table {
      border-collapse: collapse;
      width: 100%;
      margin: 12px 0;
      font-size: 10pt;
    }
    
    .message-content th,
    .message-content td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    
    .message-content th {
      background: #f5f5f5;
      font-weight: 600;
    }
    
    /* Images */
    .message-content img {
      max-width: 100%;
      height: auto;
      margin: 12px 0;
    }
    
    /* Citations */
    .citations {
      background: #f9f9f9;
      border-left: 3px solid #1976d2;
      padding: 8px 12px;
      margin-top: 12px;
      font-size: 10pt;
    }
    
    .citations-title {
      font-weight: 600;
      margin-bottom: 4px;
    }
    
    /* Links */
    a {
      color: #1976d2;
      text-decoration: none;
    }
    
    /* Divider between messages */
    .message-divider {
      border-bottom: 1px solid #eee;
      margin: 12px 0;
    }
    
    /* Model name styling */
    .model-name {
      font-size: 8pt;
      font-style: italic;
      color: #888;
      margin-top: 8px;
    }
    
    /* Print-specific styles */
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      
      body {
        padding: 0;
        margin: 0;
      }
      
      .print-header {
        margin-bottom: 16px;
      }
      
      .message {
        page-break-inside: auto !important;
        break-inside: auto !important;
        margin-bottom: 12px;
        orphans: 2;
        widows: 2;
      }
      
      .message-content {
        page-break-inside: auto !important;
        break-inside: auto !important;
      }
      
      /* Prevent forced page breaks */
      .messages-container,
      .messages-container > *,
      .message,
      .message-content,
      .message-sender,
      .message-divider,
      p, ul, ol, li, div {
        page-break-before: auto !important;
        page-break-after: auto !important;
        break-before: auto !important;
        break-after: auto !important;
        page-break-inside: auto !important;
        break-inside: auto !important;
      }
      
      @page {
        margin: 1.5cm;
      }
    }
  </style>
</head>
<body>
  <div class="print-header">
    <h1>${escapeHtml(conversationTitle)}</h1>
    <div class="meta">
      <span>Agent: ${escapeHtml(agentName)}</span> • 
      <span>Printed: ${printDate}</span>
    </div>
  </div>
  
  <div class="messages-container">
    ${clonedContent.innerHTML}
  </div>
  
  <script>
    // Auto-trigger print dialog when loaded
    window.onload = function() {
      window.print();
    };
  </script>
</body>
</html>
  `.trim();

  // Open new window and write the print document
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (printWindow) {
    printWindow.document.write(printHTML);
    printWindow.document.close();
  } else {
    alert('Unable to open print window. Please check your popup blocker settings.');
  }
}

/**
 * Process the cloned DOM content for printing
 * - Replace avatars with text labels
 * - Remove interactive elements
 * - Clean up styling
 */
function processForPrint(container: HTMLElement): void {
  // First, extract model names before removing elements
  const modelNameMap = new Map<Element, string>();
  container.querySelectorAll('[data-message-role="assistant"]').forEach(messageBox => {
    // Look for the model name in the parent structure
    const parentContainer = messageBox.parentElement;
    if (parentContainer) {
      const modelNameEl = parentContainer.querySelector('[data-model-name]');
      if (modelNameEl) {
        const modelName = modelNameEl.getAttribute('data-model-name');
        if (modelName) {
          modelNameMap.set(messageBox, modelName);
        }
      }
    }
  });

  // Remove elements that shouldn't be printed
  const elementsToRemove = [
    '[data-print-exclude]',           // Explicitly excluded elements
    '.MuiIconButton-root',            // Icon buttons (copy, actions, etc.)
    '[class*="MessageActions"]',      // Message action buttons
    '[class*="ChainOfThought"]',      // CoT bubbles
    '.MuiCircularProgress-root',      // Loading spinners
    '[class*="streaming"]',           // Streaming indicators
    '[aria-label="Copy code"]',       // Copy code buttons
    '.MuiAvatar-root',                // Avatars
    'svg',                            // SVG icons
  ];
  
  elementsToRemove.forEach(selector => {
    container.querySelectorAll(selector).forEach(el => el.remove());
  });

  // Remove ALL inline styles from all elements to prevent layout issues
  container.querySelectorAll('*').forEach(el => {
    (el as HTMLElement).removeAttribute('style');
  });

  // Find all message boxes and restructure them
  const messageBoxes = container.querySelectorAll('[data-message-role]');
  
  // Clear the container and rebuild with clean structure
  const messagesArray: HTMLElement[] = [];
  
  messageBoxes.forEach(messageBox => {
    const role = messageBox.getAttribute('data-message-role');
    const isUser = role === 'user';
    
    // Create new structure for print
    const printMessage = document.createElement('div');
    printMessage.className = 'message';
    
    // Add sender label
    const senderLabel = document.createElement('div');
    senderLabel.className = `message-sender ${isUser ? 'user' : 'assistant'}`;
    senderLabel.textContent = isUser ? 'You:' : 'Assistant:';
    printMessage.appendChild(senderLabel);
    
    // Find and clone the message content (skip avatar)
    const contentBox = messageBox.querySelector('[data-message-content]');
    if (contentBox) {
      const printContent = document.createElement('div');
      printContent.className = 'message-content';
      // Copy innerHTML but strip any remaining inline styles
      printContent.innerHTML = contentBox.innerHTML;
      printContent.querySelectorAll('*').forEach(el => {
        (el as HTMLElement).removeAttribute('style');
      });
      // Remove any model-name elements that were copied from the original content
      printContent.querySelectorAll('[data-model-name]').forEach(el => el.remove());
      printMessage.appendChild(printContent);
      
      // For assistant messages, add the model name if we captured it
      if (!isUser) {
        const modelName = modelNameMap.get(messageBox);
        if (modelName) {
          const modelDiv = document.createElement('div');
          modelDiv.className = 'model-name';
          modelDiv.textContent = modelName;
          printMessage.appendChild(modelDiv);
        }
      }
    }
    
    // Add divider
    const divider = document.createElement('div');
    divider.className = 'message-divider';
    printMessage.appendChild(divider);
    
    messagesArray.push(printMessage);
  });

  // Clear the container and add only the processed messages
  container.innerHTML = '';
  messagesArray.forEach(msg => container.appendChild(msg));
}

/**
 * Escape HTML special characters to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
